import NewGeneratePanel from "@/pages/Posts/Generate/GeneratePanel";
import { Category, ContentTopic } from "api/config/chalice-types";
import TourModal from "components/TourModal";
import useAppContext from "config/AppContext/useAppContext";
import { SHOW_TOPICS_TOUR } from "fixtures/localStorageKeys";
import { useLocalStorage } from "react-use";
import type {
  CategoryDeleteConfirmData,
  CategoryModalData,
  CombinedTopicModalState,
  GeneratePostsModalData,
  TopicModalData,
} from "../hooks/useTopicsPageModals";
import BulkAddTopics from "./BulkAddTopics";
import CategoryDeleteConfirmModal from "./CategoryDeleteConfirmModal";
import { CategoryModal } from "./CategoryModal";
import TopicFormModal from "./TopicFormModal";

interface TopicsPageModalsProps {
  isBulkEditMode: boolean;
  showModal: CombinedTopicModalState | null;
  showCategoryModal: CategoryModalData | null;
  categoryToDeleteConfirm: CategoryDeleteConfirmData | null;
  categoryName: string;
  displayTopics: ContentTopic[];
  categories: Category[];
  generateTopicKey: (topic: ContentTopic) => string;
  handleGeneratePostsForTopic: (topic?: ContentTopic) => void;
  handleUpdateTopics: (topic: ContentTopic, index?: number) => void;
  handleCategorySubmit: (selectedColor: string) => void;
  handleMoveCategoryTopicsToUncategorized: (cat: Category) => Promise<void>;
  handleDeleteCategoryAndAllTopics: (cat: Category) => Promise<void>;
  closeModal: () => void;
  closeCategoryModal: () => void;
  closeCategoryDeleteConfirmModal: () => void;
  setCategoryName: (name: string) => void;
  existingCategoryColors?: string[];
}

export const TopicsPageModals = ({
  isBulkEditMode,
  showModal,
  showCategoryModal,
  categoryToDeleteConfirm,
  categoryName,
  displayTopics,
  categories,
  generateTopicKey,
  handleGeneratePostsForTopic,
  handleUpdateTopics,
  handleCategorySubmit,
  handleMoveCategoryTopicsToUncategorized,
  handleDeleteCategoryAndAllTopics,
  closeModal,
  closeCategoryModal,
  closeCategoryDeleteConfirmModal,
  setCategoryName,
  existingCategoryColors,
}: TopicsPageModalsProps) => {
  const { realm } = useAppContext();
  const [showTopicsTour, , removeShowTopicsTour] =
    useLocalStorage<boolean>(SHOW_TOPICS_TOUR);

  return (
    <>
      {showModal?.type === "import-topics" && !isBulkEditMode && (
        <BulkAddTopics
          close={closeModal}
          isOpen={showModal.type === "import-topics"}
        />
      )}
      {showModal?.type === "generate-posts" &&
        !isBulkEditMode &&
        (showModal as GeneratePostsModalData).topic && (
          <NewGeneratePanel
            open
            onSubmit={() =>
              handleGeneratePostsForTopic(
                (showModal as GeneratePostsModalData).topic
              )
            }
            onCancel={closeModal}
            generateFrom={{
              type: "topic",
              id: (showModal as GeneratePostsModalData).topic.title ?? "",
            }}
            title={`Generate Posts for Topic: ${
              (showModal as GeneratePostsModalData).topic.title || "Untitled"
            }`}
          />
        )}

      {(showModal?.type === "new-topic" ||
        (showModal?.type === "edit-topic" &&
          (showModal as TopicModalData).topic)) &&
        !isBulkEditMode && (
          <TopicFormModal
            key={
              showModal.type === "edit-topic" &&
              (showModal as TopicModalData).topic
                ? `edit-${generateTopicKey(
                    (showModal as TopicModalData).topic as ContentTopic
                  )}-${(showModal as TopicModalData).topic?.index}`
                : "new-topic"
            }
            title={showModal.type === "new-topic" ? "Add Topic" : "Edit Topic"}
            modalData={showModal as TopicModalData}
            addTopic={handleUpdateTopics}
            updateTopic={handleUpdateTopics}
            onClose={closeModal}
            categories={categories}
            existingTopics={displayTopics}
            open
          />
        )}

      {!isBulkEditMode && (
        <>
          <CategoryModal
            modalData={showCategoryModal}
            categoryName={categoryName}
            onClose={closeCategoryModal}
            onSubmit={handleCategorySubmit}
            onNameChange={setCategoryName}
            initialColor={showCategoryModal?.initialColor}
            existingCategoryColors={existingCategoryColors}
          />

          <CategoryDeleteConfirmModal
            isOpen={categoryToDeleteConfirm !== null}
            categoryData={categoryToDeleteConfirm}
            onClose={closeCategoryDeleteConfirmModal}
            onConfirmDeleteAndMove={async (_cat) => {
              if (categoryToDeleteConfirm?.category) {
                await handleMoveCategoryTopicsToUncategorized(
                  categoryToDeleteConfirm.category
                );
              }
            }}
            onConfirmDeleteAll={async (_cat) => {
              if (categoryToDeleteConfirm?.category) {
                await handleDeleteCategoryAndAllTopics(
                  categoryToDeleteConfirm.category
                );
              }
            }}
          />
        </>
      )}

      {realm.isMarky && showTopicsTour && !isBulkEditMode && (
        <TourModal
          data={{
            title: "Automatically Generated Content Ideas",
            description:
              "This is what we base your posts on. To post about something else, edit or add here.",
          }}
          close={removeShowTopicsTour}
        />
      )}
    </>
  );
};
